/**
 * Flow diff summary helper
 * For Media Devoted | Created By Ciro Autiero
 *
 * Produces a short, human-readable summary of the changes between the live
 * flow (last GET) and the local draft. Used in the Apply confirmation dialog.
 */

import type {
  VoluumAutomationConfiguration,
  VoluumFlowResource,
  VoluumFlowUpdate,
} from '@trafficpulse/shared';

export interface FlowDiffSummary {
  hasChanges: boolean;
  bullets: string[];
}

export function summarizeFlowDiff(
  live: VoluumFlowResource | undefined,
  draft: VoluumFlowUpdate,
): FlowDiffSummary {
  if (!live) return { hasChanges: true, bullets: ['New flow draft'] };

  const bullets: string[] = [];
  if (live.name !== draft.name) bullets.push(`Name → "${draft.name}"`);
  if (live.defaultOfferRedirectMode !== draft.defaultOfferRedirectMode)
    bullets.push(`Redirect mode → ${draft.defaultOfferRedirectMode ?? 'default'}`);
  if (live.defaultPathsSmartRotation !== draft.defaultPathsSmartRotation)
    bullets.push(
      `Smart rotation → ${draft.defaultPathsSmartRotation ? 'on' : 'off'}`,
    );
  if (live.realtimeRoutingApi !== draft.realtimeRoutingApi)
    bullets.push(`Realtime routing → ${draft.realtimeRoutingApi ?? 'default'}`);

  bullets.push(...summarizeFlowAutomationDiff(live, draft));
  bullets.push(
    ...summarizePathsDiff(live.defaultPaths ?? [], draft.defaultPaths ?? []),
  );
  bullets.push(...summarizeGroupsDiff(live, draft));

  return { hasChanges: bullets.length > 0, bullets };
}

function summarizeAutomationConfigDiff(
  label: string,
  before: VoluumAutomationConfiguration | undefined,
  after: VoluumAutomationConfiguration | undefined,
): string[] {
  const prevOn = Boolean(before?.autoOptimized);
  const nextOn = Boolean(after?.autoOptimized);
  const out: string[] = [];
  if (prevOn !== nextOn) {
    out.push(`${label} → ${nextOn ? 'on' : 'off'}`);
  }
  if (nextOn && before?.calculationMethod !== after?.calculationMethod) {
    out.push(`${label} method → ${after?.calculationMethod ?? 'AUTO'}`);
  }
  return out;
}

function summarizeFlowAutomationDiff(
  live: VoluumFlowResource,
  draft: VoluumFlowUpdate,
): string[] {
  return summarizeAutomationConfigDiff(
    'Traffic distribution AI (default paths)',
    live.automationConfiguration,
    draft.automationConfiguration,
  );
}

function changedDefaultPathOptimizationCount(
  before: VoluumFlowResource['defaultPaths'],
  after: VoluumFlowUpdate['defaultPaths'],
): number {
  const n = Math.min(before.length, after.length);
  let c = 0;
  for (let i = 0; i < n; i++) {
    const prev = before[i]?.autoOptimized ?? false;
    const next = after[i]?.autoOptimized ?? false;
    if (prev !== next) c += 1;
  }
  return c;
}

function summarizePathsDiff(
  before: VoluumFlowResource['defaultPaths'],
  after: VoluumFlowUpdate['defaultPaths'],
): string[] {
  const out: string[] = [];
  if (before.length !== after.length) {
    out.push(`Default paths: ${before.length} → ${after.length}`);
  }
  const weightsBefore = before.map((p) => p.weight ?? 0).join('/');
  const weightsAfter = after.map((p) => p.weight ?? 0).join('/');
  if (weightsBefore !== weightsAfter) {
    out.push(`Path weights: ${weightsBefore} → ${weightsAfter}`);
  }
  const optCount = changedDefaultPathOptimizationCount(before, after);
  if (optCount > 0) {
    out.push(
      `Weight optimization (default paths): ${optCount} path${optCount === 1 ? '' : 's'} toggled`,
    );
  }
  return out;
}

function summarizeGroupsDiff(
  live: VoluumFlowResource,
  draft: VoluumFlowUpdate,
): string[] {
  const beforeGroups = live.conditionalPathsGroups ?? [];
  const afterGroups = draft.conditionalPathsGroups ?? [];
  const out: string[] = [];
  if (beforeGroups.length !== afterGroups.length) {
    out.push(`Conditional groups: ${beforeGroups.length} → ${afterGroups.length}`);
  }
  const n = Math.min(beforeGroups.length, afterGroups.length);
  for (let i = 0; i < n; i++) {
    const label = `Group "${afterGroups[i]?.name ?? `#${i + 1}`}" optimization`;
    out.push(
      ...summarizeAutomationConfigDiff(
        label,
        beforeGroups[i]?.automationConfiguration,
        afterGroups[i]?.automationConfiguration,
      ),
    );
  }
  return out;
}
